import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import {
    IMultiYearComparisonResponseDTO,
    IMultiYearYearPeriodDTO,
} from '@modeso/types__aumico-lib-annualreport-be';
import { ControlsState } from '../models/controls.model';
import { DeltaClassification, RowVM } from '../models/row-vm.model';
import { RowBuildResult, buildRows } from './row-vm.builder';


type BadgeVariant = 'green' | 'red' | 'gray';

// `minmax(0, 1fr)` (rather than plain `1fr`) lets the label column shrink
// BELOW the natural width of its content. Without the `0` lower bound the
// long FLI labels (e.g. "Forderungen aus Lieferungen und Leistungen") force
// the track wide enough to fit them, which pushes / wraps everything else
// when there are many year columns. With `minmax(0, …)` the track shrinks,
// the SCSS rule on `.title` truncates the text to "…", and the row stays
// one line tall.
const NAME_COLUMN = 'minmax(0, 1fr)';
const VALUE_COLUMN = '140px';
const STRUCT_COLUMN = '70px';
const DELTA_COLUMN_PCT = '140px';
const DELTA_COLUMN_CHF = '180px';

@Component({
    selector: 'harmonized-comparison-table',
    templateUrl: './harmonized-comparison-table.component.html',
    styleUrls: ['./harmonized-comparison-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HarmonizedComparisonTableComponent implements OnChanges {
    @Input() public harmonized!: IMultiYearComparisonResponseDTO;
    @Input() public controls!: ControlsState;

    @Output() public nodeToggled = new EventEmitter<string>();

    public rows: RowVM[] = [];
    public yearColumnLabels: string[] = [];
    /** Same length & order as `yearColumnLabels` — drives the header date(s) per column. */
    public yearColumnPeriods: IMultiYearYearPeriodDTO[] = [];
    public gridTemplateColumns = '';

    public ngOnChanges(changes: SimpleChanges): void {
        if ((changes['harmonized'] || changes['controls']) && this.harmonized && this.controls) {
            this.rebuildRows();
        }
    }

    public trackByRow(idx: number, row: RowVM): string {
        switch (row.kind) {
            case 'tree':
            case 'account':
                return row.nodeId;
            case 'gap':
                return `gap:${idx}`;
        }
    }

    /** Fallback display for a yearLabel when the period info is missing.
     *  ISO `YYYY-12-31` → `YYYY`; other ISO dates → `DD.MM.YYYY`; non-ISO → as-is. */
    public formatYearLabel(label: string): string {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(label);
        if (m == null) return label;
        const [, y, mo, d] = m;
        if (mo === '12' && d === '31') return y;
        return `${d}.${mo}.${y}`;
    }

    public onRowClick(row: RowVM): void {
        if (row.kind === 'tree' && row.isExpandable) {
            this.nodeToggled.emit(row.nodeId);
        }
    }

    /**
     * Map a Δ classification to the `<ham-badge-button>` variant we use.
     * Visual treatment per variant is overridden in SCSS:
     *   - green / red → solid `--green-600` / `--red-600` with white text (popping pills)
     *   - gray        → soft `--gray-100` background with `--gray-900` text (matches the
     *                   `hamBadge color="grey"` directive used elsewhere in the app)
     */
    public badgeVariantFor(classification: DeltaClassification): BadgeVariant {
        switch (classification) {
            case 'good': return 'green';
            case 'bad': return 'red';
            case 'neutral':
            case 'flat':
            default: return 'gray';
        }
    }

    private rebuildRows(): void {
        const result: RowBuildResult = buildRows(this.harmonized, this.controls);
        this.rows = result.rows;
        this.yearColumnLabels = result.yearColumnLabels;
        this.yearColumnPeriods = this.pickPeriodsForSelectedYears();
        this.gridTemplateColumns = this.buildGridTemplate(result.yearColumnLabels.length);
    }

    /** Filter `harmonized.yearPeriods` down to the currently-selected years, preserving order. */
    private pickPeriodsForSelectedYears(): IMultiYearYearPeriodDTO[] {
        const selected = new Set(this.controls.selectedYears);
        return (this.harmonized.yearPeriods ?? []).filter((p) => selected.has(p.yearLabel));
    }

    private buildGridTemplate(yearCount: number): string {
        const yearColumns = Array.from({ length: yearCount }, () =>
            this.controls.structMode ? `${VALUE_COLUMN} ${STRUCT_COLUMN}` : VALUE_COLUMN,
        ).join(' ');
        const deltaColumn = this.controls.deltaMode === 'chf' ? DELTA_COLUMN_CHF : DELTA_COLUMN_PCT;
        return `${NAME_COLUMN} ${yearColumns} ${deltaColumn}`.trim();
    }
}
